// Hooks для visual-explainer.
//
// List metadata — PowerSync `visual_analyses`.
// Detail / create / retry / playback URLs — tRPC (presigned thumbnails online).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useCurrentUser } from "@/hooks/use-session";
import { trackGenerationAccepted } from "@/lib/ecosystem-analytics";
import { useVisualAnalysesQuery } from "@/lib/powersync/queries/use-visuals-query";
import { usePowerSyncQueryShim } from "@/lib/powersync/query-shim";
import { api } from "@/lib/trpc-client";

export type VisualCategory =
	| "problem"
	| "graph"
	| "formula"
	| "handwriting"
	| "diagram"
	| "photo"
	| "other";
export type VisualStatus = "queued" | "analyzing" | "ready" | "failed";

export interface VisualListItemDto {
	id: string;
	title: string;
	category: VisualCategory | null;
	status: VisualStatus;
	thumbnailUrl: string | null;
	createdAt: string;
}

const VALID_CATEGORY = new Set<VisualCategory>([
	"problem",
	"graph",
	"formula",
	"handwriting",
	"diagram",
	"photo",
	"other",
]);

const STATUS_NORMALIZE: Record<string, VisualStatus> = {
	queued: "queued",
	pending: "queued",
	analyzing: "analyzing",
	processing: "analyzing",
	ready: "ready",
	failed: "failed",
	error: "failed",
};

/** Сколько превью подтягиваем за раз (максимум по listVisualsInputSchema). */
const THUMBNAIL_PAGE_SIZE = 100;

/** Список визуальных анализов — PowerSync. thumbnailUrl online-only (presigned). */
export function useVisuals() {
	const user = useCurrentUser();
	const online = useOnlineStatus();
	const query = useVisualAnalysesQuery(user?.id);

	// Картинки лежат в приватном бакете и открываются только по presigned-ссылке,
	// которую собирает сервер. PowerSync реплицирует метаданные без неё (в строке
	// есть лишь deprecated image_url), поэтому URL подмешиваем из tRPC-списка.
	// Офлайн запрос выключен → thumbnailUrl null → placeholder в карточке.
	// `enabled` учитывает и сеть, и наличие пользователя: список — приватный
	// (protectedProcedure), а хук зовут в том числе счётчики навигации, которые
	// монтируются на публичных страницах. Без проверки гость получал 401 на
	// каждом заходе в каталог.
	const thumbnails = api.visualExplainer.analyses.list.useQuery(
		{ limit: THUMBNAIL_PAGE_SIZE },
		{ enabled: online && user != null, staleTime: 5 * 60 * 1000 },
	);
	const thumbnailById = useMemo(() => {
		const map = new Map<string, string>();
		for (const item of thumbnails.data?.items ?? []) {
			if (item.thumbnailUrl) map.set(item.id, item.thumbnailUrl);
		}
		return map;
	}, [thumbnails.data]);

	const data = useMemo(() => {
		const items: VisualListItemDto[] = (query.data ?? []).map((r) => ({
			id: r.id,
			title: r.title ?? "",
			category: VALID_CATEGORY.has(r.category as VisualCategory)
				? (r.category as VisualCategory)
				: null,
			status: STATUS_NORMALIZE[r.status] ?? "queued",
			thumbnailUrl: thumbnailById.get(r.id) ?? null,
			createdAt: r.created_at,
		}));
		return { items };
	}, [query.data, thumbnailById]);
	return usePowerSyncQueryShim(query, data);
}

export function useVisualDetail(id: string | undefined) {
	return api.visualExplainer.analyses.getById.useQuery(
		{ queryId: id ?? "" },
		{
			enabled: Boolean(id),
			// Анализ исполняется в Inngest-воркере. Статус-страница поллит
			// getById до терминального статуса (ready/failed). Socket-
			// инвалидация в $id.tsx ускоряет реакцию, но поллинг — надёжный
			// fallback: Socket.IO best-effort (событие теряется при обрыве),
			// а здесь UI гарантированно догонит status из БД.
			refetchInterval: (query) => {
				const status = query.state.data?.query.status;
				if (status === "ready" || status === "failed") return false;
				return 2500;
			},
		},
	);
}

/**
 * Стартует async анализ: presignedUpload → PUT файла → create query.
 * Inngest workflow `generateVisualAnalysis` подхватывает event и обновляет
 * row до status='ready'. Socket.IO event `visual:ready` → invalidate.
 */
export function useCreateVisual() {
	const queryClient = useQueryClient();
	const getPresigned = api.visualExplainer.analyses.getPresignedUpload.useMutation();
	const createMutation = api.visualExplainer.analyses.create.useMutation({
		onSuccess: (result) => {
			trackGenerationAccepted("other", result);
			void queryClient.invalidateQueries({
				queryKey: [["visualExplainer", "analyses", "list"]],
			});
		},
	});

	return useMutation({
		mutationFn: async (args: {
			file: Blob;
			mime: "image/jpeg" | "image/png" | "image/webp" | "image/gif";
			width: number;
			height: number;
			sizeBytes: number;
			crop?: { x: number; y: number; width: number; height: number };
			userHint?: string;
			language: string;
			title?: string;
			materialId?: string;
		}) => {
			const presigned = await getPresigned.mutateAsync({
				mime: args.mime,
				sizeBytes: args.sizeBytes,
			});
			const putRes = await fetch(presigned.uploadUrl, {
				method: "PUT",
				headers: { "content-type": args.mime },
				body: args.file,
			});
			if (!putRes.ok) {
				throw new Error(`S3 upload failed: ${putRes.status}`);
			}
			return createMutation.mutateAsync({
				imageS3Key: presigned.s3Key,
				mime: args.mime,
				width: args.width,
				height: args.height,
				sizeBytes: args.sizeBytes,
				crop: args.crop,
				userHint: args.userHint,
				language: args.language,
				title: args.title,
				materialId: args.materialId,
			});
		},
	});
}

/**
 * Повтор упавшего/зависшего анализа. Бэкенд делает race-safe re-claim статуса
 * (failed → pending) и переэмитит Inngest event — getById после инвалидации
 * увидит pending → страница покажет лоадер. Квота повторно НЕ списывается.
 */
export function useRetryVisual() {
	const queryClient = useQueryClient();
	return api.visualExplainer.analyses.retry.useMutation({
		onSuccess: (_data, vars) => {
			void queryClient.invalidateQueries({
				queryKey: [
					["visualExplainer", "analyses", "getById"],
					{ input: { queryId: vars.queryId } },
				],
			});
			void queryClient.invalidateQueries({
				queryKey: [["visualExplainer", "analyses", "list"]],
			});
		},
	});
}

export function useDeleteVisual() {
	const queryClient = useQueryClient();
	return api.visualExplainer.analyses.delete.useMutation({
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [["visualExplainer", "analyses", "list"]],
			});
		},
	});
}

/**
 * Список постраничных превью PDF-материала (carry-over Phase 3 #2).
 * Используется fromMaterial picker в UploadDialog: пользователь выбирает
 * материал → видит сетку thumbnails страниц → выбирает страницу для анализа.
 *
 * Возвращает пустой items[] если extractMaterialFigures workflow ещё не
 * отработал (тогда picker показывает emptyState «превью извлекаются»).
 */
export function useMaterialFigures(materialId: string | undefined, poll = false) {
	return api.visualExplainer.materialFigures.listByMaterial.useQuery(
		{ materialId: materialId ?? "" },
		{
			enabled: Boolean(materialId),
			// extractMaterialFigures не эмитит completion-эвент — поллим, пока идёт
			// извлечение и фигур ещё нет (громкая деградация: rules/logging.md §13a).
			refetchInterval: (query) =>
				poll && (query.state.data?.items.length ?? 0) === 0 ? 3000 : false,
		},
	);
}

/**
 * On-demand триггер извлечения постраничных превью PDF (carry-over Phase 3 #2,
 * закрыт 2026-06-04). Picker дёргает при выборе материала; idempotency на стороне
 * Inngest-функции (materialId) защищает от дублей. Возвращает `{ enqueued }` —
 * false, если фигуры уже есть либо материал не PDF / ещё не готов.
 */
export function useEnsureMaterialFigures() {
	return api.visualExplainer.materialFigures.ensureExtracted.useMutation();
}

/**
 * Сохранить готовый анализ в конспект.
 * Без `noteId` (default) — серверный mutation создаёт НОВЫЙ конспект с TipTap
 * `aiBlock` (type=visual-analysis). С `noteId` — append в существующий.
 * В обоих случаях эмитится `note:ready` для realtime-sync открытого редактора.
 */
export function useSaveVisualToNote() {
	const queryClient = useQueryClient();
	return api.visualExplainer.analyses.saveToNote.useMutation({
		onSuccess: (data) => {
			void queryClient.invalidateQueries({
				queryKey: [["notes", "get"], { input: { noteId: data.noteId } }],
			});
			void queryClient.invalidateQueries({
				queryKey: [["notes", "list"]],
			});
		},
	});
}
